import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { snapshotUrl, streamUrl, type Camera, type StreamFps, type StreamProfile } from '../lib/api';
import { mulaiHeartbeat, pakaiAliran } from '../lib/streamClient';
import { tunjukKamera } from '../lib/prewarm';
import { useBahasa } from '../i18n';

/**
 * Jeda sebelum mencoba menyambung ulang setelah `<img>` gagal. Naik bertahap
 * supaya kamera yang benar-benar mati tidak dihujani permintaan, tapi tetap
 * pulih sendiri tanpa ada yang menekan refresh — §G8.1 mensyaratkan itu untuk
 * TV pos satpam yang tidak punya operator.
 */
const RETRY_START_MS = 2_000;
const RETRY_MAX_MS = 30_000;

/**
 * Umur maksimum poster (§D3.2, 23 Agustus 2026).
 *
 * Poster adalah **frame terakhir**, bukan gambar langsung. Selama koneksi MJPEG
 * belum mengirim apa pun ia jauh lebih baik daripada kotak hitam — tapi kalau
 * koneksinya tidak pernah datang, gambar diam yang menetap di layar pantau
 * berbahaya: operator akan membacanya sebagai "sekarang". Jadi ia punya umur.
 * Sesudah ini tile kembali ke keadaan jujurnya ("Loading…" / "Reconnecting…").
 */
const POSTER_MAX_MS = 10_000;

/** Sama dengan `duration-200` pada `<img>` stream — dipakai untuk melepas poster. */
const FADE_MS = 200;

/**
 * Fade masuk poster (§D3.5) — gambar pertama yang terlihat saat halaman
 * berganti.
 *
 * Sejak poster dipasang, tile memang terisi cepat; yang tersisa adalah
 * **caranya**: gambar muncul seketika dari hitam, dan tiga puluh gambar yang
 * menyentak bersamaan terbaca sebagai kedipan, bukan sebagai pergantian
 * halaman. Angkanya sengaja pendek — ini menghaluskan tepi perpindahan, bukan
 * animasi yang perlu diperhatikan; di atas ±150 ms ia mulai terasa sebagai
 * "lambat" alih-alih "halus".
 */
const POSTER_FADE_MS = 120;

interface Props {
  camera: Camera;
  /** Tile yang tidak aktif WAJIB berhenti streaming (§D3.2). */
  streaming: boolean;
  profile?: StreamProfile;
  /** Laju yang diminta klien (§D3.2). `null` = penuh. */
  fps?: StreamFps;
  selected?: boolean;
  /** Ditampilkan saat profil HD ditolak kamera (§D1.2). */
  limitedQuality?: boolean;
  onSelect?: () => void;
  onFullscreen?: () => void;
  /**
   * Klik di mana saja pada tile (23 Agustus 2026).
   *
   * Bawaannya tidak ada, dan itu disengaja: §G7 no.5 melarang seluruh tile jadi
   * tombol karena di TV pos satpam salah pencet berarti layar pantau
   * ditinggalkan tanpa ada yang sadar. Yang mencabut alasan itu hanya satu
   * keadaan — **slideshow yang sedang dijeda**. Di sana sudah ada orang yang
   * mengambil alih, dan yang dia cari justru jalan tercepat ke kamera yang
   * mencurigakan: *"Kalau gw di posisi Slideshow - paused, gw gak bisa klik →
   * langsung lari ke kamera tersebut ya? Workflow-nya terlalu lama kalau mesti
   * ngeliat cepet ke hal yang mencurigakan."*
   *
   * Dipakai **menggantikan** ikon pojok, bukan bersamanya: di mode layar penuh
   * ikon pojok tile pertama duduk persis di bawah tombol "Stop (Esc)".
   */
  onTileClick?: () => void;
  /**
   * Boleh memanaskan worker HD saat penunjuk berhenti di tile ini (§D3.6)?
   *
   * Terpisah dari "tile ini bisa dibuka" sejak 25 Agustus 2026: selama
   * slideshow berjalan tilenya memang bisa diklik, tapi penunjuk yang
   * tergeletak di atas grid yang berpindah sendiri bukan tanda ada yang
   * melihat — dan satu worker HD memakan 9,7% core (§D1.1).
   */
  praPanas?: boolean;
  /**
   * Tinggi tile mengikuti **lebarnya**, bukan tinggi sel gridnya (31 Agustus
   * 2026, ponsel tegak — `§G8`).
   *
   * Bawaannya `false`, dan itu yang benar untuk hampir semua layar: tile wajib
   * muat di dalam selnya supaya seluruh grid terlihat sekaligus tanpa
   * menggulung, dan `maxHeight: 100%` yang mengerjakannya — sel yang lebih
   * pendek daripada 16:9 selebar sel membuat tile-nya menyempit sambil menjaga
   * rasio. Di satu kolom pada ponsel tegak, penyempitan itu justru yang
   * dikeluhkan: gambarnya berhenti jauh sebelum tepi layar dan sisa kiri-kanannya
   * jadi batang hitam. Di sana tile mengisi lebar penuh dan gridnya yang
   * menggulung.
   */
  isiLebar?: boolean;
}

/** "3 jam lalu" — jarak, bukan stempel waktu. Yang ditanya orang seberapa lama. */
function jarakWaktu(iso: string, t: ReturnType<typeof useBahasa>['t']): string {
  const detik = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (detik < 60) return t('just now');
  const menit = Math.floor(detik / 60);
  if (menit < 60) return t('{n} min ago', { n: menit });
  const jam = Math.floor(menit / 60);
  if (jam < 24) return t('{n}h ago', { n: jam });
  return t('{n}d ago', { n: Math.floor(jam / 24) });
}

function jamWib(iso: string): string {
  return new Date(iso).toLocaleTimeString('id-ID', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Jakarta',
  });
}

/**
 * Konteks kamera padam (§D9.3) — **empat baris, dan berhenti di empat**.
 *
 * Tile pada grid 4×4 lebarnya ±320px; baris kelima berarti mengecilkan hurufnya
 * sampai tidak terbaca dari jarak TV pos satpam (§G7). Yang keluar dari daftar
 * ini — uptime, jumlah segmen, ukuran folder — dibaca di halaman kamera, bukan
 * di dinding.
 */
function KonteksPadam({ camera }: { camera: Camera }) {
  const { t } = useBahasa();

  // `undefined` = endpointnya memang tidak mengirimkannya. Diam adalah jawaban
  // yang benar di situ; menulis "belum pernah tersambung" untuk data yang cuma
  // tidak ikut diminta jauh lebih buruk daripada tidak menulis apa-apa.
  if (camera.lastRecordingAt === undefined && camera.recorder === undefined) return null;

  /**
   * Kamera yang belum pernah hidup sama sekali tidak menampilkan tiga baris
   * sebagai "—" berjajar, melainkan satu kalimat (§D9.3). Deretan tanda hubung
   * membuat orang mengira datanya yang hilang, padahal keadaannya yang memang
   * belum ada.
   */
  const belumPernah =
    !camera.lastRecordingAt && !camera.lastError && (camera.recorder?.attempt ?? 0) === 0;
  if (belumPernah) {
    return (
      <p className="mt-1 text-[11px] leading-4 text-muted">{t('Never connected yet.')}</p>
    );
  }

  const percobaan = camera.recorder?.attempt ?? 0;
  const jedaDetik =
    camera.recorder?.retryInMs !== undefined
      ? Math.round(camera.recorder.retryInMs / 1000)
      : null;

  return (
    <dl className="mt-1 space-y-0.5 text-[11px] leading-4 text-muted">
      {/* Kamera View Only tidak menampilkan baris ini (§D2): ia akan selalu
          berbunyi "terakhir merekam: tidak pernah", dan itu terbaca sebagai
          kerusakan pada kamera yang justru bekerja sesuai maksudnya. */}
      {camera.recordEnabled && camera.lastRecordingAt && (
        <div className="truncate">
          <dt className="inline">{t('Last recorded')}: </dt>
          <dd className="inline">
            {jamWib(camera.lastRecordingAt)} — {jarakWaktu(camera.lastRecordingAt, t)}
          </dd>
        </div>
      )}

      {/* Pesan terpetakan (§D7.2 no.3), bukan `stderr` mentah — aturan hak akses
          §D7.3 berlaku di sini juga, dan tile dilihat semua peran. */}
      {camera.lastError && (
        <div className="truncate" title={camera.lastError}>
          <dt className="inline">{t('Last error')}: </dt>
          <dd className="inline">{camera.lastError}</dd>
        </div>
      )}

      {percobaan > 0 && (
        <div className="truncate">
          <dt className="inline">{t('Reconnect')}: </dt>
          <dd className="inline">
            {jedaDetik === null
              ? t('attempt {n}', { n: percobaan })
              : t('attempt {n}, next in {s}s', { n: percobaan, s: jedaDetik })}
          </dd>
        </div>
      )}

      {camera.measuredMbps !== null && (
        <div className="truncate">
          <dt className="inline">{t('Last bitrate')}: </dt>
          <dd className="inline">{t('{n} Mbps while it was up', { n: camera.measuredMbps.toFixed(2) })}</dd>
        </div>
      )}
    </dl>
  );
}

/** Titik status §G3.1 — selalu berpasangan dengan label teks, tidak hanya warna. */
function StatusDot({ camera }: { camera: Camera }) {
  const { t } = useBahasa();
  const map = {
    // REC hanya untuk kamera yang benar-benar menulis berkas (§D2).
    //
    // Sebelumnya syaratnya cuma `isEnabled`, dan kamera View Only ikut memakai
    // titik merah berdenyut. Titik itu bukan hiasan status — ia janji bahwa apa
    // yang sedang terlihat masih bisa dibuka lagi besok, dan untuk kamera View
    // Only janji itu palsu: tidak ada satu pun berkas yang ditulis untuknya.
    // Syaratnya sekarang sama persis dengan `CameraHealthBar` (§G11), supaya
    // satu kamera tidak bercerita dua hal berbeda di dua tempat.
    online:
      camera.isEnabled && camera.recordEnabled
        ? { color: 'bg-red-600', label: 'REC', pulse: true }
        : { color: 'bg-green-600', label: 'ONLINE', pulse: false },
    offline: { color: 'bg-muted', label: 'OFFLINE', pulse: false },
    error: { color: 'bg-amber-500', label: 'ERROR', pulse: false },
  } as const;
  /**
   * Pemeliharaan menang atas status apa pun (§G3.1).
   *
   * Warnanya `--brand-primary` — satu-satunya warna palet yang **belum** dipakai
   * sebagai status, jadi ia sudah berbeda dari keempat baris lain tanpa
   * menambah hex baru di luar §G3. Ia juga terbaca sebagai "ini keadaan yang
   * dibuat aplikasi" alih-alih "ini kerusakan", dan itu memang bedanya
   * pemeliharaan dari offline.
   */
  const s = camera.maintenanceSince
    ? ({ color: 'bg-accent', label: 'MAINTENANCE', pulse: false } as const)
    : map[camera.status];

  return (
    <span className="flex items-center gap-1.5" title={camera.lastError ?? undefined}>
      <span
        // Diameter ≥10px, terbaca dari 3 meter (§G7 no.3).
        className={`h-2.5 w-2.5 shrink-0 rounded-full ${s.color} ${s.pulse ? 'animate-rec' : ''}`}
        aria-hidden="true"
      />
      <span className="text-xs font-semibold tracking-wide">{t(s.label)}</span>
    </span>
  );
}

export function CameraTile({
  camera,
  streaming,
  profile = 'sub',
  fps = null,
  selected = false,
  limitedQuality = false,
  onSelect,
  onFullscreen,
  onTileClick,
  praPanas = true,
  isiLebar = false,
}: Props) {
  const { t } = useBahasa();
  const [nonce, setNonce] = useState(() => Date.now());
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  /**
   * Poster: frame terakhir dari worker tile yang **memang sudah panas**
   * (`GET /snapshot/:id`, §C2), dipasang sementara koneksi MJPEG menyusul.
   *
   * Ini yang menutup kedipan saat orang menekan Next berturut-turut pada 5×4
   * atau 6×5: satu pergantian halaman memutus 30 koneksi dan membangun 30 yang
   * baru, dan sepanjang itu seluruh layar kosong. Terukur sebelum poster
   * dipasang: 30 dari 30 tile kosong seketika, penuh lagi 1,1–3,3 detik.
   *
   * Pola yang sama dipakai Lensa lama, dan di sana ia gratis karena tile-nya
   * memang berkas JPEG yang di-poll. Di sini ongkosnya satu permintaan ~20 KB
   * per tile per pemasangan — terukur ±20 ms — dan hanya sekali, bukan aliran.
   */
  const [posterHidup, setPosterHidup] = useState(true);
  const [posterGagal, setPosterGagal] = useState(false);
  const [posterMuat, setPosterMuat] = useState(false);
  // Dikunci saat tile dipasang: poster tidak boleh ikut berganti tiap render.
  const posterNonce = useRef(Date.now());
  const retryMs = useRef(RETRY_START_MS);
  const retryTimer = useRef<number | undefined>(undefined);
  const imgRef = useRef<HTMLImageElement>(null);

  // Kamera offline tidak dicoba di-stream sama sekali: prosesnya memang tidak
  // punya frame untuk dikirim, dan `<img>` yang menggantung hanya menahan
  // koneksi tanpa hasil.
  const shouldStream = streaming && camera.isEnabled && camera.status !== 'offline';

  useEffect(() => {
    if (!shouldStream) {
      setLoaded(false);
      setFailed(false);
      retryMs.current = RETRY_START_MS;
    }
    return () => window.clearTimeout(retryTimer.current);
  }, [shouldStream]);

  // Umur poster — lihat `POSTER_MAX_MS`.
  useEffect(() => {
    if (!shouldStream) return;
    const t = window.setTimeout(() => setPosterHidup(false), POSTER_MAX_MS);
    return () => window.clearTimeout(t);
  }, [shouldStream]);

  /**
   * Poster bertahan sampai gambar langsung selesai memudar masuk.
   *
   * Melepasnya tepat saat `onLoad` menyala terlihat benar di kode dan salah di
   * layar: pada detik itu `<img>` stream baru **memulai** transisi 200 ms dari
   * `opacity-0`, jadi selama transisi tidak ada satu pun lapisan yang pekat dan
   * latar hitam tile menembus. Terukur sebagai kedipan pendek — hitungan tile
   * yang sudah bergambar sempat turun 16 → 13 di tengah pergantian halaman,
   * yaitu persis kedipan yang poster ini ada untuk menghapusnya.
   */
  useEffect(() => {
    if (!loaded) return;
    const t = window.setTimeout(() => setPosterHidup(false), FADE_MS + 60);
    return () => window.clearTimeout(t);
  }, [loaded]);

  /**
   * Batalkan permintaan MJPEG secara **eksplisit** saat tile berhenti (§D3.4).
   *
   * Melepas `<img>` dari DOM tidak menjamin browser memutus koneksi
   * `multipart/x-mixed-replace` yang memang tidak pernah selesai; mengosongkan
   * `src` yang benar-benar membatalkannya. Ini sisi klien dari perbaikan yang
   * sama: sisi servernya mengakhiri aliran yang klien-nya berhenti menyapa,
   * karena Zoraxy tidak meneruskan kabar putus ke hulu.
   */
  useEffect(() => {
    const img = imgRef.current;
    return () => {
      if (img) img.src = '';
    };
  }, [shouldStream, profile, nonce]);

  // Sapaan hanya hidup selama tile ini memang mengalirkan gambar (§D3.4).
  useEffect(() => {
    if (!shouldStream) return;
    return mulaiHeartbeat();
  }, [shouldStream]);

  /*
   * Dan sapaan itu ikut membawa kamera ini selama tile-nya menampilkannya
   * (§D3.4 aturan 3). Pembatalan di sisi klien saja tidak cukup: `img.src=''`
   * dikerjakan browser sebisanya, sedangkan daftar ini membuat server yang
   * memutuskan — dan server-lah satu-satunya yang benar-benar memegang
   * koneksinya.
   */
  useEffect(() => {
    if (!shouldStream) return;
    return pakaiAliran(camera.id, profile);
  }, [shouldStream, camera.id, profile]);

  // Profil berganti (sub → main saat fullscreen) berarti sumber baru.
  useEffect(() => setNonce(Date.now()), [profile]);

  function handleError() {
    setLoaded(false);
    setFailed(true);
    /**
     * Jedanya diacak ±30%, dan itu bukan hiasan.
     *
     * Tiga puluh tile pada satu halaman gagal **bersamaan** — satu pemutusan di
     * hulu memutus semuanya sekaligus — jadi tanpa acakan ketiga puluhnya
     * mencoba lagi pada milidetik yang sama, berulang-ulang: gelombang 30
     * permintaan tiap 2 detik yang menahan keadaan gagalnya sendiri tetap
     * hidup. Terukur 24 Agu 2026 saat menekan Next bertubi-tubi: 1.120
     * permintaan dalam satu jendela 10 detik, sebagian besar dari percobaan
     * ulang yang seirama. Acakan memecah gelombang itu jadi aliran datar.
     */
    const jeda = retryMs.current * (0.7 + Math.random() * 0.6);
    retryTimer.current = window.setTimeout(() => {
      setFailed(false);
      setNonce(Date.now());
      retryMs.current = Math.min(retryMs.current * 2, RETRY_MAX_MS);
    }, jeda);
  }

  /**
   * Poster tampil hanya di jendela sempit yang memang butuh: sedang mencoba
   * streaming, koneksinya belum gagal, umurnya belum habis, dan snapshot-nya
   * sendiri ada. Ia **tidak** langsung dilepas saat frame pertama datang —
   * lihat efek `loaded` di atas; ia duduk di bawah dan tidak terlihat lagi
   * begitu gambar langsung pekat.
   *
   * `!failed` penting: begitu koneksi putus, tile berpindah ke "Reconnecting…"
   * dan poster **dilepas**. Gambar diam yang bertahan di atas kamera yang
   * sedang putus adalah kebohongan yang paling mahal di aplikasi ini.
   */
  const posterAktif = shouldStream && posterHidup && !failed && !posterGagal;

  /**
   * Tile ini bisa dibuka layar penuh sekarang — lewat ikon pojok, atau lewat
   * seluruh tile di mode layar penuh. Yang menentukan boleh-tidaknya pra-panas
   * bukan ini sendirian, tapi `praPanas` di atas: selama slideshow berjalan
   * tilenya bisa diklik namun tidak ada penunjuk yang berarti apa-apa.
   */
  const bisaDibuka = Boolean(onFullscreen || onTileClick);

  return (
    <div
      className={`group relative isolate overflow-hidden rounded-lg bg-black ring-1 ring-line ${
        selected ? 'ring-2 ring-accent' : ''
      } ${camera.status === 'offline' && !camera.maintenanceSince ? 'opacity-60' : ''}`}
      // Rasio terkunci 16:9 supaya tile tidak pernah "melompat" saat gambar
      // datang atau hilang (§G7 no.4).
      //
      // `maxHeight` yang membuatnya benar-benar MUAT: kalau selnya lebih pendek
      // daripada 16:9 selebar sel, browser mengecilkan lebarnya sambil menjaga
      // rasio. Tanpa itu tile selalu selebar sel, menjuntai ke bawah, dan sisa
      // ruang menumpuk jadi satu lubang kosong di bawah baris pertama.
      //
      // `isiLebar` mencabut batas itu untuk ponsel tegak — lihat propertinya.
      style={{
        aspectRatio: '16 / 9',
        width: '100%',
        maxWidth: '100%',
        maxHeight: isiLebar ? undefined : '100%',
        margin: 'auto',
      }}
      // Penunjuk yang berhenti di sebuah tile hampir selalu mendahului klik
      // pada tile itu — jadi worker HD-nya dinyalakan sekarang, sementara
      // orangnya masih melihat (§D3.6). Hanya untuk tile yang memang bisa
      // dibuka sekarang: saat slideshow berjalan tidak ada penunjuk yang
      // berarti apa-apa, dan kamera yang tidak di-stream tidak punya yang
      // perlu dipanaskan.
      onPointerEnter={
        bisaDibuka && shouldStream && praPanas ? () => tunjukKamera(camera.id) : undefined
      }
      onPointerLeave={
        bisaDibuka && shouldStream && praPanas ? () => tunjukKamera(null) : undefined
      }
    >
      {/* Gambarnya sendiri BUKAN tombol (§G7 no.5) — kecuali `onTileClick`
          diberikan, dan itu hanya terjadi di mode layar penuh. Aksinya
          selebihnya ada di ikon pojok kanan atas: seluruh tile yang bisa diklik
          membuat "lihat sebentar" dan "pindah halaman" tidak bisa dibedakan,
          dan di TV pos satpam salah pencet berarti layar pantau ditinggalkan. */}
      <div className="absolute inset-0 h-full w-full">
        {/* Poster duduk DI BAWAH gambar langsung — keduanya `absolute inset-0`,
            dan yang belakangan di DOM menang. Jadi begitu frame MJPEG pertama
            datang, ia menimpa poster tanpa satu pun frame hitam di antaranya.

            Dilepas dari DOM begitu tidak dipakai lagi, bukan sekadar
            disembunyikan: 30 JPEG yang menetap di memori tiap halaman adalah
            ongkos yang tidak dibayar siapa pun. */}
        {posterAktif && (
          <img
            src={snapshotUrl(camera.id, posterNonce.current)}
            alt=""
            aria-hidden="true"
            onLoad={() => setPosterMuat(true)}
            onError={() => setPosterGagal(true)}
            className={`absolute inset-0 h-full w-full object-contain transition-opacity ${
              posterMuat ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ transitionDuration: `${POSTER_FADE_MS}ms` }}
          />
        )}

        {shouldStream && !failed && (
          <img
            ref={imgRef}
            // `fps` ikut jadi bagian kunci: mengubah laju berarti URL baru, dan
            // tanpa remount `<img>` browser tetap menempel pada koneksi lama.
            key={`${camera.id}-${profile}-${fps ?? 'full'}-${nonce}`}
            src={streamUrl(camera.id, profile, nonce, fps)}
            alt=""
            onLoad={() => {
              setLoaded(true);
              retryMs.current = RETRY_START_MS;
            }}
            onError={handleError}
            className={`h-full w-full object-contain transition-opacity duration-200 ${
              loaded ? 'opacity-100' : 'opacity-0'
            }`}
          />
        )}

        {/* Keadaan selain "gambar mengalir" punya desainnya sendiri (§G7 no.7). */}
        {!shouldStream && (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-3 text-center text-sm text-muted">
            <span>
              {camera.maintenanceSince
                ? t('MAINTENANCE')
                : camera.status === 'offline'
                  ? t('OFFLINE')
                  : !camera.isEnabled
                    ? t('DISABLED')
                    : t('Paused')}
            </span>

            {/* Catatan operatornya ditampilkan apa adanya — ini yang dibaca
                orang yang cuma menonton dan bertanya kenapa kameranya mati
                (§D10.2). Tile-nya TIDAK diredupkan: kamera yang sengaja
                dikerjakan orang tidak boleh terlihat sama dengan yang mati
                sendiri. */}
            {camera.maintenanceSince && camera.maintenanceNote && (
              <span className="mt-1 max-w-full truncate text-xs text-muted">
                {camera.maintenanceNote}
              </span>
            )}
            {/* Konteksnya hanya untuk kamera yang MATI SENDIRI. Kamera yang
                dimatikan orang lewat `is_enabled` tidak punya penyebab untuk
                dijelaskan — menampilkan "error terakhir" di sana berarti
                menyodorkan kejadian minggu lalu sebagai sebab keadaan hari ini. */}
            {/* Konteks padam TIDAK ditampilkan untuk kamera dalam pemeliharaan
                (§D9.3): "error terakhir" dari sebelum orangnya mulai bekerja
                bukan sebab keadaan sekarang. */}
            {camera.status === 'offline' && camera.isEnabled && !camera.maintenanceSince && (
              <KonteksPadam camera={camera} />
            )}
          </div>
        )}
        {/* "Loading…" hanya kalau memang tidak ada apa-apa untuk dilihat.
            Menumpuknya di atas poster cuma membuat gambar yang sudah tampil
            terlihat seperti gambar yang gagal. "Reconnecting…" tetap muncul —
            poster memang sudah dilepas di keadaan itu. */}
        {shouldStream && !loaded && !posterAktif && (
          <span
            className="absolute inset-0 flex items-center justify-center text-sm text-muted"
            role="status"
          >
            {failed ? t('Reconnecting…') : t('Loading…')}
          </span>
        )}
      </div>

      {/* Lapisan klik seluruh tile. `z-0` menahannya di bawah ikon pojok
          (`z-10`) supaya keduanya masih bisa hidup bersama kalau suatu saat
          dipakai bersamaan, dan overlay nama sudah `pointer-events-none`. */}
      {onTileClick && (
        <button
          type="button"
          onClick={onTileClick}
          aria-label={t('View {nama} fullscreen', { nama: camera.name })}
          title={t('{nama} — fullscreen', { nama: camera.name })}
          className="absolute inset-0 z-0 h-full w-full cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-white"
        />
      )}

      {/* Ikon aksi §G7 no.5. Selalu terlihat, tidak hanya saat hover: perangkat
          sentuh tidak punya hover sama sekali, dan aksi yang tidak kelihatan
          sama saja dengan tidak ada. Di penunjuk presisi kontrasnya menguat
          saat hover — itu penegasan, bukan syarat munculnya. */}
      {(onFullscreen || onSelect) && (
        <div className="absolute right-1.5 top-1.5 z-10 flex gap-1.5">
          <Link
            to={`/videos/${camera.id}`}
            onClick={() => onSelect?.()}
            title={t('{nama} — recordings', { nama: camera.name })}
            aria-label={t('Open recordings for {nama}', { nama: camera.name })}
            className="flex h-11 w-11 items-center justify-center rounded-md bg-black/50 text-white ring-1 ring-white/20 transition-colors hover:bg-black/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            {/* Segitiga play, bukan panah unduh: tombol ini membuka halaman
                Playback, tidak mengunduh apa pun. Ikon unduh yang dulu di sini
                menjanjikan berkas yang tidak pernah datang.

                Diisi penuh (`fill`), bukan hanya garis — segitiga bergaris tipis
                pada kotak 20px kehilangan bentuknya di atas gambar kamera yang
                ramai, dan yang harus terbaca dari jauh justru bentuknya. */}
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
              <path d="M8 5.14a1 1 0 0 1 1.53-.85l9.72 6.86a1 1 0 0 1 0 1.7L9.53 19.71A1 1 0 0 1 8 18.86z" />
            </svg>
          </Link>
          {onFullscreen && (
            <button
              type="button"
              /* Sasaran fokus kursor papan ketik dashboard — lihat
                 `fokuskanTile` di DashboardPage. Remote TV cuma punya panah dan
                 OK, jadi kursor tile harus memegang fokus DOM yang sebenarnya
                 supaya OK punya sesuatu untuk ditekan. */
              data-tile-fs=""
              onClick={() => {
                onSelect?.();
                onFullscreen();
              }}
              title={t('{nama} — fullscreen', { nama: camera.name })}
              aria-label={t('View {nama} fullscreen', { nama: camera.name })}
              className="flex h-11 w-11 items-center justify-center rounded-md bg-black/50 text-white ring-1 ring-white/20 transition-colors hover:bg-black/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M8 3H5a2 2 0 0 0-2 2v3" />
                <path d="M16 3h3a2 2 0 0 1 2 2v3" />
                <path d="M8 21H5a2 2 0 0 1-2-2v-3" />
                <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Overlay nama & status. `pointer-events-none` supaya seluruh tile tetap
          bisa diklik — target kliknya jadi seluas tile, jauh di atas 44px.

          Namanya duduk di tengah bawah dan diangkat sedikit dari tepi: hampir
          semua kamera membakar nama dan jamnya sendiri ke pojok KIRI bawah
          gambar, jadi label kiri-bawah bertumpuk dengan tulisan yang tidak bisa
          kita pindahkan. Status tetap di kanan, tempat yang selalu kosong. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-end bg-gradient-to-t from-black/80 to-transparent px-2 pb-1.5 pt-8 text-white">
        {/* Minimal 14px, terbaca dari 3 meter (§G7 no.3). `inset-x-10` menjaga
            jarak dari label status di kanan sekaligus membuat `truncate` punya
            lebar yang pasti. */}
        <span className="absolute inset-x-10 bottom-5 truncate text-center text-sm font-medium drop-shadow">
          {camera.name}
        </span>
        <StatusDot camera={camera} />
      </div>

      {camera.status === 'error' && camera.lastError && (
        <span
          className="pointer-events-none absolute left-2 top-2 flex items-center gap-1 rounded bg-amber-500 px-1.5 py-0.5 text-xs font-semibold text-black"
          title={camera.lastError}
        >
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
          </svg>
          {t('Needs attention')}
        </span>
      )}

      {/* Di bawah ikon aksi, bukan di sudut yang sama: keduanya bisa muncul
          bersamaan pada kamera §D1.2, dan badge yang tertindih ikon tidak
          terbaca. */}
      {limitedQuality && (
        <span className="pointer-events-none absolute right-1.5 top-14 rounded bg-black/70 px-1.5 py-0.5 text-xs text-white">
          {t('Limited quality')}
        </span>
      )}
    </div>
  );
}
